import { useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownSelectorProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  label: string;
  placeholder?: string;
}

export const DropdownSelector = ({
  options,
  value,
  onChange,
  onBlur,
  error,
  touched,
  label,
  placeholder = 'Select an option',
}: DropdownSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasError = touched && error && !isOpen;

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <fieldset className="fieldset mb-0 gap-2 w-full">
      <legend className="fieldset-legend input-label">{label}</legend>
      <Listbox
        value={value}
        onChange={(val) => {
          onChange(val);
          setIsOpen(false);
        }}
      >
        <div className="relative">
          <ListboxButton
            onClick={() => setIsOpen(true)}
            onBlur={() => {
              setIsOpen(false);
              onBlur?.();
            }}
            className={`input input-dropdown flex items-center justify-between w-full px-3 py-2 ${hasError ? 'border-input-error' : ''}`}
          >
            <span
              className={
                selectedOption ? 'text-default' : 'text-[var(--color-text-input-placeholder)]'
              }
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ListboxButton>

          <ListboxOptions className="absolute border border-[var(--color-dropdown-options-border)] shadow-sm bg-[var(--color-text-input-background)] z-10 mt-1 max-h-60 w-full overflow-auto rounded-md py-1 focus:outline-none">
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={({ focus }) =>
                  `relative text-default text-sm cursor-pointer select-none py-2 pl-3 pr-9 ${
                    focus ? 'bg-[var(--color-selected-option-background)]' : ''
                  }`
                }
              >
                {({ selected }) => (
                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                    {option.label}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
      {hasError && <span className="input-error">{error}</span>}
    </fieldset>
  );
};
